import * as React from "react";
import {
    TopToolbar,
    useDataProvider,
    useListContext,
    useResourceContext,
} from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Button from "@mui/material/Button";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useMutation } from "react-query";
import PdfMyIcon from "../myIcons/PdfMyIcon";
import ExcelMyIcon from "../myIcons/ExcelMyIcon";

const DownloadAction = ({
    field,
    order,
    extraActions = null,
    disabled = false,
    detailOnly = false,
}) => {
    const { filterValues, sort } = useListContext();
    const resource = useResourceContext();
    const dataProvider = useDataProvider();
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef(null);

    // State untuk menangani posisi popper
    const [placement, setPlacement] = React.useState("bottom-end");

    const { data, mutate } = useMutation((params) => {
        let init = resource + params.path;
        let hasQuery = false;

        // tambahkan filter bila ada
        if (Object.keys(filterValues).length) {
            init += "?filter=" + encodeURIComponent(JSON.stringify(filterValues));
            hasQuery = true;
        }

        // tambahkan parameter extra (summary/detail)
        if (params.extra && typeof params.extra === "object") {
            Object.entries(params.extra).forEach(([key, val]) => {
                init += (hasQuery ? "&" : "?") + `${key}=${val}`;
                hasQuery = true;
            });
        }

        // tambahkan sort
        init +=
            (hasQuery ? "&" : "?") +
            "sort=" +
            encodeURIComponent(
                JSON.stringify([
                    (field ? field : sort.field) + " " + (order ? order : sort.order),
                ])
            );

        return dataProvider.getListNoParams(init);
    });

    const handleToggle = () => {
        if (disabled) return;
        // Cek posisi window untuk menentukan placement yang optimal
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            
            // Jika dekat dengan sisi kanan, gunakan bottom-start
            if (rect.right > windowWidth - 200) {
                setPlacement("bottom-start");
            } else {
                setPlacement("bottom-end");
            }
        }
        setOpen((prev) => !prev);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) return;
        setOpen(false);
    };

    const handleDownload = (path, extra = {}) => {
        mutate({ path, extra });
    };

    function handleListKeyDown(event) {
        if (event.key === "Tab") {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    }

    React.useEffect(() => {
        if (data && data.link) {
            setOpen(false);
            const newlink = document.createElement("a");
            newlink.href = data.link;
            newlink.target = "_blank";
            document.body.appendChild(newlink);
            newlink.click();
            newlink.remove();
        }
    }, [data]);

    // Handler untuk resize window
    React.useEffect(() => {
        const handleResize = () => {
            if (open && anchorRef.current) {
                const rect = anchorRef.current.getBoundingClientRect();
                const windowWidth = window.innerWidth;
                
                if (rect.right > windowWidth - 200) {
                    setPlacement("bottom-start");
                } else {
                    setPlacement("bottom-end");
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [open]);

    return (
        <TopToolbar
            sx={{
                minHeight: "54px !important",
                "& .MuiButtonBase-root": { border: "0.1rem solid" },
                position: 'relative',
                gap: 1,
            }}
        >
            {extraActions}
            <Button
                ref={anchorRef}
                sx={{ 
                    width: "fit-content", 
                    lineHeight: "1.5",
                    minWidth: '100px',
                }}
                id="composition-button"
                aria-controls={open ? "composition-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                disabled={disabled}
                startIcon={<DownloadIcon />}
                size="small"
            >
                Export
            </Button>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement={placement}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 8,
                        },
                    },
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top-end', 'top-start', 'bottom-start'],
                        },
                    },
                ]}
                sx={{ 
                    zIndex: 1300, 
                    width: 180,
                    maxWidth: 'calc(100vw - 16px)',
                    '& .MuiPaper-root': {
                        maxWidth: '100%',
                    }
                }}
            >
                {({ TransitionProps }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: 'right top',
                        }}
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                minWidth: 180,
                                maxWidth: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    autoFocusItem={open}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                    dense
                                    sx={{
                                        py: 0,
                                        '& .MuiMenuItem-root': {
                                            minHeight: 40,
                                            py: 1,
                                        }
                                    }}
                                >
                                    {!detailOnly && (
                                        <>
                                            <MenuItem
                                                onClick={() => handleDownload("/pdf", { type: "summary" })}
                                                sx={{ border: "none !important", '&:hover': { backgroundColor: 'action.hover' } }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <PdfMyIcon />
                                                </ListItemIcon>
                                                <ListItemText primary="PDF Summary" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                            </MenuItem>
                                        </>
                                    )}

                                    <MenuItem
                                        onClick={() => handleDownload("/pdf", { type: "detail" })}
                                        sx={{ 
                                            border: "none !important",
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <PdfMyIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={detailOnly ? "PDF" : "PDF Detail"} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                    </MenuItem>

                                    {!detailOnly && (
                                        <MenuItem
                                            onClick={() => handleDownload("/excel", { type: "summary" })}
                                            sx={{ border: "none !important", '&:hover': { backgroundColor: 'action.hover' } }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <ExcelMyIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Excel Summary" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                        </MenuItem>
                                    )}

                                    <MenuItem
                                        onClick={() => handleDownload("/excel", { type: "detail" })}
                                        sx={{ 
                                            border: "none !important",
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            }
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <ExcelMyIcon />
                                        </ListItemIcon>
                                        <ListItemText primary={detailOnly ? "Excel" : "Excel Detail"} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </TopToolbar>
    );
};

export default DownloadAction;
